/**
 * The per-area access a membership carries (020 § Access grants, the owner's decision of
 * 2026-09-21), as the client reads it.
 *
 * An area with no grant is **absent**, not disabled: its rail mode has no tile, its sidebar
 * rows are not rendered, and its routes answer 403 if reached another way. The server is the
 * decision either way; this module exists so the client draws the same conclusion it would,
 * and draws it in one place rather than per screen.
 *
 * The grants on `/me` are already **resolved**, so a workspace owner arrives here holding
 * every area at its top level (`clients` at portal, `dev` and `hr` at write, 028 FR-028-001)
 * and nothing on the client knows about the owner bypass. That is
 * deliberate: the bypass is stated once, in the domain crate.
 *
 * What `/me` does not resolve is the role's half of a decision. The server holds a key only
 * when the role's permissions and the area's grant both hold it (020 FR-020-176), and no key
 * the `clients` area gates is among a guest's (`WORKSPACE_GUEST_KEYS` in the domain crate), so
 * a guest's `clients` grant admits them to nothing. `accessOf` drops it, which is why every
 * screen reads the caller's grants through that one function.
 */

/** The closed list of areas, in the order a settings screen lists them. */
export const ACCESS_AREAS = ["dev", "clients", "hr"] as const;
export type AccessArea = (typeof ACCESS_AREAS)[number];

/**
 * The three levels a grant carries, weakest first, so a level's index is how it compares: each
 * implies every level before it (portal implies write, which implies read, 028 FR-028-001).
 */
export const ACCESS_LEVELS = ["read", "write", "portal"] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

/** Whether a grant of `level` may be set on `area`: portal exists on clients alone (028 FR-028-004), as the domain crate's `AccessArea::admits` decides. */
export function areaTakes(area: AccessArea, level: AccessLevel): boolean {
  return level !== "portal" || area === "clients";
}

export function isAccessArea(value: string): value is AccessArea {
  return (ACCESS_AREAS as readonly string[]).includes(value);
}

export function isAccessLevel(value: string): value is AccessLevel {
  return (ACCESS_LEVELS as readonly string[]).includes(value);
}

/** One grant, as `/me` and the member list both state it. */
export interface AccessGrant {
  readonly area: string;
  readonly level: string;
}

/** The structural shape of the `/me` read this module needs. */
interface MeWithAccess {
  readonly memberships: readonly {
    readonly slug: string;
    readonly role: string;
    readonly access?: readonly AccessGrant[];
  }[];
}

/** The areas whose every gated key the role's own permissions leave out, so a grant for one admits the role to nothing. */
const AREAS_NO_KEY_OF: Readonly<Record<string, readonly AccessArea[]>> = { guest: ["clients"] };

/** One filtered list per stored list, so a caller's grants keep their identity across renders and a memo keyed on them holds. */
const effective = new WeakMap<readonly AccessGrant[], readonly AccessGrant[]>();

/**
 * The grants the caller holds in one workspace as the server decides them, empty when they
 * hold none: the stored grants less any area the membership's role holds no key of, which
 * is the `clients` area for a guest.
 */
export function accessOf(me: MeWithAccess | undefined, ws: string): readonly AccessGrant[] {
  const membership = me?.memberships.find((candidate) => candidate.slug === ws);
  const stored = membership?.access ?? [];
  const keyless = AREAS_NO_KEY_OF[membership?.role ?? ""];
  if (keyless === undefined) return stored;
  const admitsNothing = (grant: AccessGrant) => isAccessArea(grant.area) && keyless.includes(grant.area);
  if (!stored.some(admitsNothing)) return stored;
  const cached = effective.get(stored);
  if (cached !== undefined) return cached;
  const held = stored.filter((grant) => !admitsNothing(grant));
  effective.set(stored, held);
  return held;
}

/** The level held in `area`, or undefined when the area is not granted at all. */
export function levelIn(grants: readonly AccessGrant[], area: AccessArea): AccessLevel | undefined {
  const level = grants.find((grant) => grant.area === area)?.level;
  return level !== undefined && isAccessLevel(level) ? level : undefined;
}

/**
 * Whether `grants` satisfy a requirement for `level` in `area`. A level satisfies a
 * requirement for any level before it in `ACCESS_LEVELS` and never one after it, which is the
 * same implication the domain crate applies.
 */
export function holdsArea(grants: readonly AccessGrant[], area: AccessArea, level: AccessLevel): boolean {
  const held = levelIn(grants, area);
  if (held === undefined) return false;
  return ACCESS_LEVELS.indexOf(held) >= ACCESS_LEVELS.indexOf(level);
}

/**
 * Whether the caller reads the clients area in `ws`: the Team dashboard's By client card and the
 * route's skeleton both ask this, so the wait draws the cards the page will (2026-09-29).
 */
export function readsClients(me: MeWithAccess | undefined, ws: string): boolean {
  return holdsArea(accessOf(me, ws), "clients", "read");
}

/** Whether `area` is visible at all, which is what a rail tile and a sidebar row ask. */
export function seesArea(grants: readonly AccessGrant[], area: AccessArea): boolean {
  return levelIn(grants, area) !== undefined;
}
